import type { FunnelProps } from './funnel';

/**
 * What kind of device and browser a visit is on, as a few closed values (easy-signup handoff,
 * 2026-10-04): ad clicks open inside Facebook's own browser, where Google sign-in is refused, and
 * the journey log could not tell that visit from Safari's.
 *
 * Read from the user agent here and reduced to the allowlisted values in funnel.ts. The user agent
 * itself is never sent or stored. `in_app` also decides whether the sign-up screen offers Google.
 */
export type DeviceProps = Required<Pick<FunnelProps, 'device' | 'os' | 'browser' | 'in_app' | 'viewport'>>;

interface Env {
  ua: string;
  width: number;
  /** A touch screen: an iPad asks for the desktop site and says "Macintosh". */
  touch: boolean;
}

function env(): Env {
  if (typeof navigator === 'undefined') return { ua: '', width: 0, touch: false };
  return {
    ua: navigator.userAgent ?? '',
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
    touch: (navigator.maxTouchPoints ?? 0) > 1,
  };
}

export function inAppBrowser(ua: string): DeviceProps['in_app'] {
  if (/FBAN|FBAV|FB_IAB|FBIOS|FB4A/.test(ua)) return 'facebook';
  if (/Instagram/i.test(ua)) return 'instagram';
  if (/musical_ly|BytedanceWebview|TikTok/i.test(ua)) return 'tiktok';
  return 'none';
}

export function deviceProps({ ua, width, touch }: Env = env()): DeviceProps {
  const iPadAsMac = /Macintosh/.test(ua) && touch;
  const os: DeviceProps['os'] = /iPhone|iPad|iPod/.test(ua) || iPadAsMac
    ? 'ios'
    : /Android/.test(ua)
      ? 'android'
      : /Windows/.test(ua)
        ? 'windows'
        : /Macintosh|Mac OS X/.test(ua)
          ? 'macos'
          : /Linux|CrOS|X11/.test(ua)
            ? 'linux'
            : 'other';
  const device: DeviceProps['device'] = /iPad/.test(ua) || iPadAsMac || (os === 'android' && !/Mobile/.test(ua)) || /Tablet/.test(ua)
    ? 'tablet'
    : /Mobi|iPhone|iPod|Android/.test(ua)
      ? 'mobile'
      : 'desktop';
  const browser: DeviceProps['browser'] = /SamsungBrowser/.test(ua)
    ? 'samsung'
    : /Edg(e|A|iOS)?\//.test(ua)
      ? 'edge'
      : /Firefox|FxiOS/.test(ua)
        ? 'firefox'
        : /Chrome|CriOS|Chromium/.test(ua)
          ? 'chrome'
          : /Safari/.test(ua) && /Version\//.test(ua)
            ? 'safari'
            : 'other';
  const viewport: DeviceProps['viewport'] = width < 380 ? '<380' : width < 430 ? '380-430' : width < 768 ? '430-768' : width < 1024 ? '768-1024' : '1024+';
  return { device, os, browser, in_app: inAppBrowser(ua), viewport };
}
